import { useState } from 'react';
import {
  ArrowRight,
  Package,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { InventoryItem } from '@/hooks/useInventory';

type TransferDirection = 'boh_to_foh' | 'foh_to_boh';

interface BOHTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inventoryItem: InventoryItem | null;
  onTransfer: (data: {
    inventory_item_id: string;
    transfer_type: TransferDirection;
    quantity: number;
    notes?: string;
  }) => Promise<void>;
  isTransferring?: boolean;
}

export function BOHTransferDialog({
  open,
  onOpenChange,
  inventoryItem,
  onTransfer,
  isTransferring = false,
}: BOHTransferDialogProps) {
  const [direction, setDirection] = useState<TransferDirection>('boh_to_foh');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const bohStock = inventoryItem?.quantity_boh ?? 0;
  const fohStock = inventoryItem?.quantity_foh ?? inventoryItem?.quantity_on_hand ?? 0;
  const maxTransfer = direction === 'boh_to_foh' ? bohStock : fohStock;

  const handleSubmit = async () => {
    if (!inventoryItem) return;

    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      setError('Please enter a valid quantity greater than 0');
      return;
    }

    if (qty > maxTransfer) {
      setError(`Cannot transfer more than ${maxTransfer} units`);
      return;
    }

    setError(null);

    try {
      await onTransfer({
        inventory_item_id: inventoryItem.id,
        transfer_type: direction,
        quantity: qty,
        notes: notes.trim() || undefined,
      });

      // Reset form
      setQuantity('');
      setNotes('');
      setDirection('boh_to_foh');
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transfer failed');
    }
  };

  const handleClose = () => {
    setQuantity('');
    setNotes('');
    setError(null);
    setDirection('boh_to_foh');
    onOpenChange(false);
  };

  if (!inventoryItem) return null;

  return (
    <ResponsiveModal open={open} onOpenChange={handleClose}>
      <ResponsiveModalContent>
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Transfer Inventory
          </ResponsiveModalTitle>
          <ResponsiveModalDescription>
            Move stock between Back of House (storage) and Front of House (selling floor)
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody>
          <div className="space-y-6">
            {/* Product Info */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              {inventoryItem.product?.image_url ? (
                <img
                  src={inventoryItem.product.image_url}
                  alt={inventoryItem.product?.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                  <Package className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
              <div>
                <h3 className="font-semibold">{inventoryItem.product?.name}</h3>
                {inventoryItem.product?.sku && (
                  <p className="text-sm text-muted-foreground">SKU: {inventoryItem.product.sku}</p>
                )}
              </div>
            </div>

            {/* Current Stock Display */}
            <div className="grid grid-cols-2 gap-4">
              <div className={cn(
                "p-4 rounded-lg border-2 transition-colors",
                direction === 'boh_to_foh' ? 'border-blaze-500 bg-blaze-50 dark:bg-blaze-950/20' : 'border-border'
              )}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Back of House</span>
                  <Badge variant="outline">Storage</Badge>
                </div>
                <p className="text-3xl font-bold">{bohStock}</p>
                {direction === 'boh_to_foh' && (
                  <p className="text-xs text-muted-foreground mt-1">Source</p>
                )}
              </div>

              <div className={cn(
                "p-4 rounded-lg border-2 transition-colors",
                direction === 'foh_to_boh' ? 'border-blaze-500 bg-blaze-50 dark:bg-blaze-950/20' : 'border-border'
              )}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Front of House</span>
                  <Badge variant="outline">Selling Floor</Badge>
                </div>
                <p className="text-3xl font-bold">{fohStock}</p>
                {direction === 'foh_to_boh' && (
                  <p className="text-xs text-muted-foreground mt-1">Source</p>
                )}
              </div>
            </div>

            {/* Direction Toggle */}
            <div className="flex justify-center">
              <div className="inline-flex rounded-lg border p-1 bg-muted/50">
                <Button
                  type="button"
                  variant={direction === 'boh_to_foh' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDirection('boh_to_foh')}
                  className="gap-2"
                >
                  BOH
                  <ArrowRight className="h-4 w-4" />
                  FOH
                </Button>
                <Button
                  type="button"
                  variant={direction === 'foh_to_boh' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDirection('foh_to_boh')}
                  className="gap-2"
                >
                  FOH
                  <ArrowRight className="h-4 w-4" />
                  BOH
                </Button>
              </div>
            </div>

            {/* Quantity Input */}
            <div className="space-y-2">
              <Label htmlFor="transfer-quantity">Quantity to Transfer</Label>
              <div className="flex gap-2">
                <Input
                  id="transfer-quantity"
                  type="number"
                  min="0"
                  max={maxTransfer}
                  step="1"
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter quantity"
                  className="text-lg"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setQuantity(maxTransfer.toString())}
                  disabled={maxTransfer <= 0}
                >
                  Max ({maxTransfer})
                </Button>
              </div>
              {maxTransfer <= 0 && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  No stock available in {direction === 'boh_to_foh' ? 'Back of House' : 'Front of House'}
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="transfer-notes">Notes (Optional)</Label>
              <Textarea
                id="transfer-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for transfer..."
                rows={2}
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Preview */}
            {quantity && parseFloat(quantity) > 0 && parseFloat(quantity) <= maxTransfer && (
              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                  After transfer:
                </p>
                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">BOH:</span>{' '}
                    <strong>
                      {direction === 'boh_to_foh'
                        ? bohStock - parseFloat(quantity)
                        : bohStock + parseFloat(quantity)}
                    </strong>
                  </div>
                  <div>
                    <span className="text-muted-foreground">FOH:</span>{' '}
                    <strong>
                      {direction === 'boh_to_foh'
                        ? fohStock + parseFloat(quantity)
                        : fohStock - parseFloat(quantity)}
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isTransferring ||
              !quantity ||
              parseFloat(quantity) <= 0 ||
              parseFloat(quantity) > maxTransfer
            }
          >
            {isTransferring ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Transferring...
              </>
            ) : (
              <>
                <ArrowRight className="mr-2 h-4 w-4" />
                Transfer Stock
              </>
            )}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}









